import { z } from 'zod';

export const roleSchema = z.enum(['ADMIN', 'SUPPORT', 'CLIENT']);
/** Re-inferred role union (identical to `Role` in types/auth.ts). Kept local to avoid barrel collision. */
export type RoleSchemaType = z.infer<typeof roleSchema>;

const nameField = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(60, `${label} must be 60 characters or fewer`);

export const userAdminCreateSchema = z
  .object({
    email: z.string().trim().toLowerCase().email('Must be a valid email address'),
    fullName: z
      .string()
      .trim()
      .min(1, 'Full name is required')
      .max(120, 'Full name must be 120 characters or fewer'),
    firstName: nameField('First name').optional(),
    lastName: nameField('Last name').optional(),
    role: roleSchema,
    companyId: z.string().min(1).optional().nullable(),
    password: z.string().min(12, 'Password must be at least 12 characters').optional(),
  })
  .strict();
export type UserAdminCreateInput = z.infer<typeof userAdminCreateSchema>;

/**
 * Admin user updates cannot change email and cannot set password directly;
 * password changes are user-initiated through the reset flow.
 */
export const userAdminUpdateSchema = z
  .object({
    fullName: z
      .string()
      .trim()
      .min(1, 'Full name is required')
      .max(120, 'Full name must be 120 characters or fewer')
      .optional(),
    firstName: nameField('First name').optional(),
    lastName: nameField('Last name').optional(),
    role: roleSchema.optional(),
    companyId: z.string().min(1).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type UserAdminUpdateInput = z.infer<typeof userAdminUpdateSchema>;

export const userReassignSchema = z
  .object({
    userId: z.string().min(1, 'User id is required'),
    companyId: z.string().min(1).nullable(),
  })
  .strict();
export type UserReassignInput = z.infer<typeof userReassignSchema>;

export const userAdminSummarySchema = z.object({
  id: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  role: roleSchema,
  companyId: z.string().nullable(),
  isActive: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type UserAdminSummary = z.infer<typeof userAdminSummarySchema>;

/**
 * Self-service profile update (PATCH /api/auth/me/profile). fullName is
 * composed server-side from firstName + lastName.
 */
export const profileUpdateSchema = z
  .object({
    firstName: nameField('First name'),
    lastName: nameField('Last name'),
    email: z.string().trim().toLowerCase().email('Must be a valid email address'),
    phone: z.string().trim().max(30).nullable().optional(),
  })
  .strict();
export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;
